/**
 * Network Status Composable
 *
 * Provides reactive network status detection for Vue components.
 * Uses the Navigator.onLine API and listens for online/offline events.
 */

import { ref, onMounted, onUnmounted, readonly } from 'vue';

export type NetworkState = 'online' | 'offline';

export interface NetworkStatusReturn {
  /** Whether the device is currently online */
  isOnline: Readonly<typeof isOnline>;
  /** Whether the device was offline and just came back online */
  wasOffline: Readonly<typeof wasOffline>;
  /** Timestamp when the device went offline (null if online) */
  offlineSince: Readonly<typeof offlineSince>;
  /** Current network state as string */
  networkState: Readonly<typeof networkState>;
  /** Reset the wasOffline flag */
  resetWasOffline: () => void;
}

// Module-level refs for shared state across components
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);
const wasOffline = ref(false);
const offlineSince = ref<Date | null>(null);
const networkState = ref<NetworkState>(isOnline.value ? 'online' : 'offline');

// Track if listeners are already set up
let listenersInitialized = false;

// Store handler references for cleanup during HMR
let onlineHandler: (() => void) | null = null;
let offlineHandler: (() => void) | null = null;

/**
 * Network status composable for detecting online/offline state
 *
 * @example
 * ```vue
 * <script setup>
 * import { useNetworkStatus } from 'src/composables/useNetworkStatus';
 *
 * const { isOnline, wasOffline, resetWasOffline } = useNetworkStatus();
 *
 * watch(isOnline, (online) => {
 *   if (online && wasOffline.value) {
 *     // Reconnect logic
 *     resetWasOffline();
 *   }
 * });
 * </script>
 * ```
 */
export function useNetworkStatus(): NetworkStatusReturn {
  const handleOnline = () => {
    console.info('[NetworkStatus] Network came online');
    isOnline.value = true;
    networkState.value = 'online';
    // Mark that we were offline (useful for triggering sync)
    if (offlineSince.value !== null) {
      wasOffline.value = true;
    }
    offlineSince.value = null;
  };

  const handleOffline = () => {
    console.warn('[NetworkStatus] Network went offline');
    isOnline.value = false;
    networkState.value = 'offline';
    offlineSince.value = new Date();
  };

  onMounted(() => {
    if (typeof window === 'undefined') return;

    // Only add listeners once globally
    if (!listenersInitialized) {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      listenersInitialized = true;
    }
  });

  onUnmounted(() => {
    // Note: We don't remove listeners since other components may still use them
    // This is intentional for a singleton-like behavior
  });

  const resetWasOffline = () => {
    wasOffline.value = false;
  };

  return {
    isOnline: readonly(isOnline),
    wasOffline: readonly(wasOffline),
    offlineSince: readonly(offlineSince),
    networkState: readonly(networkState),
    resetWasOffline,
  };
}

/**
 * Initialize network status listeners (call once at app startup)
 * This is useful for setting up listeners outside of Vue components.
 */
export function initializeNetworkStatus(): void {
  if (typeof window === 'undefined') return;
  if (listenersInitialized) return;

  // Store handlers for later cleanup during HMR
  onlineHandler = () => {
    console.info('[NetworkStatus] Network came online');
    isOnline.value = true;
    networkState.value = 'online';
    if (offlineSince.value !== null) {
      wasOffline.value = true;
    }
    offlineSince.value = null;
  };

  offlineHandler = () => {
    console.warn('[NetworkStatus] Network went offline');
    isOnline.value = false;
    networkState.value = 'offline';
    offlineSince.value = new Date();
  };

  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);

  listenersInitialized = true;
  console.debug('[NetworkStatus] Listeners initialized');
}

/**
 * Cleanup network status listeners (call during HMR dispose)
 * This removes all listeners to prevent memory leaks during hot reload.
 */
export function cleanupNetworkStatus(): void {
  if (typeof window === 'undefined') return;

  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;
  }
  if (offlineHandler) {
    window.removeEventListener('offline', offlineHandler);
    offlineHandler = null;
  }
  listenersInitialized = false;
  console.debug('[NetworkStatus] Listeners cleaned up');
}

/**
 * Get current online status without Vue reactivity
 * Useful for non-component code
 */
export function getNetworkStatus(): {
  isOnline: boolean;
  wasOffline: boolean;
  offlineSince: Date | null;
} {
  return {
    isOnline: isOnline.value,
    wasOffline: wasOffline.value,
    offlineSince: offlineSince.value,
  };
}

/**
 * Reset the wasOffline flag programmatically
 */
export function resetWasOfflineFlag(): void {
  wasOffline.value = false;
}

// HMR cleanup - reset state when module is replaced
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    console.debug('[NetworkStatus] HMR dispose - cleaning up');
    cleanupNetworkStatus();
  });
}
